'use client'

import { toast } from 'sonner'

/**
 * Convert HEIC/HEIF files to JPEG on the client side
 */
export async function convertHeicToJpeg(file: File): Promise<File | null> {
    try {
        const extension = file.name.split('.').pop()?.toLowerCase()
        
        // Check if file is HEIC/HEIF
        if (!extension || !['heic', 'heif'].includes(extension)) {
            return null // Not a HEIC/HEIF file
        }

        // Only run on client-side
        if (typeof window === 'undefined') {
            console.warn('HEIC conversion skipped: not running in browser')
            return file // Return original file on server-side
        }

        console.log(`Converting ${extension.toUpperCase()} file to JPEG: ${file.name}`)

        // Dynamic import for client-side only
        const heic2any = (await import('heic2any')).default as any

        // Convert using heic2any
        const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9, // High quality (90%)
        })

        // Handle the result (heic2any can return Blob or Blob[])
        const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob

        // Create new File object with .jpg extension
        const baseName = file.name.replace(/\.(heic|heif)$/i, '')
        const newFileName = `${baseName}.jpg`
        
        const convertedFile = new File([blob], newFileName, {
            type: 'image/jpeg',
            lastModified: file.lastModified,
        })

        console.log(`Successfully converted ${file.name} to ${newFileName}`)
        
        return convertedFile
    } catch (error) {
        console.error('Error converting HEIC/HEIF to JPEG:', error)
        
        // Show user-friendly error
        const fileName = file.name
        toast.error('Conversion Failed', {
            description: `Could not convert "${fileName}". Please try uploading a different format.`,
            duration: 5000
        })
        
        return null // Return null to exclude this file from upload
    }
}

/**
 * Process an array of files, converting HEIC/HEIF files to JPEG
 */
export async function processFilesForUpload(files: File[]): Promise<File[]> {
    const processedFiles: File[] = []
    const conversionPromises = files.map(async (file) => {
        const convertedFile = await convertHeicToJpeg(file)
        return convertedFile || file // Use converted file if successful, otherwise original
    })
    
    const results = await Promise.allSettled(conversionPromises)
    
    results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
            processedFiles.push(result.value)
        } else {
            // File conversion failed, exclude from upload
            console.warn(`Excluding file from upload: ${files[index].name}`)
        }
    })
    
    return processedFiles
}

/**
 * Check if any files in the array are HEIC/HEIF files
 */
export function hasHeicFiles(files: File[]): boolean {
    return files.some(file => {
        const extension = file.name.split('.').pop()?.toLowerCase()
        return extension && ['heic', 'heif'].includes(extension)
    })
}

/**
 * Count the number of converted files by comparing original and processed arrays
 */
export function getConversionCount(originalFiles: File[], processedFiles: File[]): number {
    return processedFiles.filter((processedFile, index) => 
        processedFile.name !== originalFiles[index]?.name
    ).length
}
